import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Building2, ChevronDown, Check, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ImgOffline } from "@/components/offline/ImgOffline";

interface LinkedCompany {
  company_id: string;
  company_name: string;
  company_logo: string | null;
  is_usable?: boolean;
}

interface PeritoCompanySwitcherProps {
  companies: LinkedCompany[];
  currentCompanyId: string | null;
  onSwitch: (companyId: string) => Promise<void>;
  /**
   * Dónde se pinta (tanda 5, A9 / T01-04):
   *  · "inline"  texto blanco pequeño con flecha: bajo el título de la cabecera del
   *               móvil y de la tableta, y en la barra lateral en el sitio del
   *               nombre fijo de la empresa.
   *  · "icon"    barra lateral plegada: solo el edificio.
   *  · "pill"    la píldora de antes (sobre fondo claro).
   */
  variant?: "pill" | "inline" | "icon";
}

/**
 * Conmutador de empresa del perito.
 *
 * Tanda 5 (26-09-2026):
 *  · Mientras cambia, una capa a pantalla completa «Cambiando a X…» (T01-17): el
 *    cambio tarda de 1 a 10 s y la pantalla seguía enseñando la empresa anterior
 *    con solo un spinner tenue.
 *  · Si falla, se dice (T15-13): antes no pasaba nada.
 *  · Las empresas sin suscripción salen marcadas y no se pueden elegir (M40).
 */
export function PeritoCompanySwitcher({ companies, currentCompanyId, onSwitch, variant = "pill" }: PeritoCompanySwitcherProps) {
  const { t } = useTranslation();
  const [switchingTo, setSwitchingTo] = useState<LinkedCompany | null>(null);

  if (companies.length <= 1) return null;

  const current = companies.find((c) => c.company_id === currentCompanyId);

  const handleSwitch = async (c: LinkedCompany) => {
    if (c.company_id === currentCompanyId || switchingTo || c.is_usable === false) return;
    setSwitchingTo(c);
    try {
      await onSwitch(c.company_id);
    } catch (err) {
      console.error("Switch company error:", err);
      toast.error(t("perito.multiCompany.switchError"));
    } finally {
      setSwitchingTo(null);
    }
  };

  const trigger =
    variant === "inline" ? (
      <DropdownMenuTrigger
        disabled={!!switchingTo}
        className="w-full flex items-center gap-1 text-left font-['Mulish'] font-bold text-[11.5px] 2xl:text-[13px] text-white/[0.82] hover:text-white transition-colors"
        aria-label={t("perito.invitacion.cambiarEmpresa")}
        data-testid="perito-company-switcher"
      >
        <span className="truncate">{current?.company_name || "—"}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
      </DropdownMenuTrigger>
    ) : variant === "icon" ? (
      <DropdownMenuTrigger
        disabled={!!switchingTo}
        className="flex items-center justify-center w-9 h-9 rounded-[11px] bg-white/10 hover:bg-white/25 text-white transition-colors"
        aria-label={t("perito.invitacion.cambiarEmpresa")}
        title={current?.company_name}
        data-testid="perito-company-switcher"
      >
        <Building2 className="h-4 w-4" />
      </DropdownMenuTrigger>
    ) : (
      <DropdownMenuTrigger
        disabled={!!switchingTo}
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors",
          "hover:bg-muted border border-border bg-background",
          switchingTo && "opacity-60",
        )}
        aria-label={t("perito.invitacion.cambiarEmpresa")}
        data-testid="perito-company-switcher"
      >
        {switchingTo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}
        <span className="truncate max-w-[120px]">{current?.company_name || "—"}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </DropdownMenuTrigger>
    );

  return (
    <>
      <DropdownMenu>
        {trigger}
        <DropdownMenuContent align="start" className="w-60">
          {companies.map((c) => {
            const bloqueada = c.is_usable === false;
            return (
              <DropdownMenuItem
                key={c.company_id}
                onClick={() => void handleSwitch(c)}
                disabled={bloqueada}
                className={cn(
                  "flex items-center gap-2 cursor-pointer",
                  c.company_id === currentCompanyId && "bg-accent",
                )}
              >
                {c.company_logo ? (
                  <ImgOffline src={c.company_logo} alt="" className="h-5 w-5 object-contain rounded" />
                ) : (
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{c.company_name}</span>
                  {bloqueada && (
                    <span className="block text-[11px] text-muted-foreground">{t("perito.invitacion.sinSuscripcion")}</span>
                  )}
                </span>
                {c.company_id === currentCompanyId && <Check className="h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {switchingTo &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-3 bg-background/95 backdrop-blur-sm"
            role="status"
            aria-live="polite"
            data-testid="perito-switching-overlay"
          >
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-[15px] font-semibold text-foreground">
              {t("perito.invitacion.cambiandoA", { empresa: switchingTo.company_name })}
            </p>
          </div>,
          document.body,
        )}
    </>
  );
}
